import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'

import type { OldManagementAccountResponse } from '@aeris/contracts'
import { MONEY_SCALE, formatMoney, fromString } from '@aeris/money'
import {
  QueryState,
  Section,
  StatTile,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { fetchOldManagementAccount } from '../lib/api/exchanges.ts'
import { numberValue, type ExportColumn } from '../lib/export/table-export.ts'

import { ExportMenu } from './export-menu.tsx'

/**
 * A conta com a gestão antiga — F216.
 *
 * O dono: "a Elite que teve que dar o produto pra depois o cara pagar eles de
 * volta". O cliente da loja anterior volta com a peça, ganha vale (troca sem
 * venda, F148) e gasta o vale em produto da Elite. O que ele JÁ gastou é o
 * que a gestão antiga ressarce; o que está em aberto ainda pode virar produto.
 *
 * O uso sai primeiro do vale antigo (F214), e um vale tirado à mão não conta
 * como produto entregue. A conta é de hoje, da loja inteira: vale é do
 * cliente, não da filial.
 */

type Customer = OldManagementAccountResponse['customers'][number]

const money = (wire: string): string =>
  formatMoney(fromString(wire, MONEY_SCALE))

const EXPORT: readonly ExportColumn<Customer>[] = [
  { id: 'name', label: 'Cliente', value: (row) => row.name },
  {
    id: 'exchanges',
    label: 'Trocas',
    value: (row) => numberValue(String(row.exchanges)),
  },
  {
    id: 'issued',
    label: 'Vale antigo emitido',
    value: (row) => numberValue(row.issued),
  },
  {
    id: 'used',
    label: 'Já usado (a cobrar)',
    value: (row) => numberValue(row.used),
  },
  { id: 'open', label: 'Em aberto', value: (row) => numberValue(row.open) },
]

export const OldManagementAccount = (): ReactNode => {
  const account = useQuery({
    queryKey: ['exchanges', 'old-management'],
    queryFn: ({ signal }) => fetchOldManagementAccount(signal),
  })
  const data = account.data
  const customers = data?.customers ?? []

  return (
    <Section
      title="Gestão antiga — a cobrar"
      description="O vale das trocas de produto de fora e o dado à mão como da gestão antiga. O que o cliente já gastou em produto da Elite é o que a gestão antiga deve; o em aberto ainda pode virar produto."
      action={
        customers.length === 0 ? undefined : (
          <ExportMenu
            fileName="gestao-antiga"
            sheetName="Gestão antiga"
            columns={EXPORT}
            fetchPage={async () =>
              await Promise.resolve({
                items: customers,
                total: customers.length,
              })
            }
            pageSize={Math.max(customers.length, 1)}
            total={customers.length}
          />
        )
      }
    >
      <QueryState
        isPending={account.isPending}
        isError={account.isError}
        error={account.error}
        onRetry={() => {
          void account.refetch()
        }}
        what="a conta da gestão antiga"
        isEmpty={false}
        empty={null}
      >
        {data === undefined ? null : (
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <StatTile
                label="Já usado — a cobrar"
                value={
                  <span className="font-(--font-mono) tabular-nums text-(--danger)">
                    {money(data.used)}
                  </span>
                }
                hint="Produto que a Elite já entregou contra vale antigo."
              />
              <StatTile
                label="Em aberto"
                value={
                  <span className="font-(--font-mono) tabular-nums">
                    {money(data.open)}
                  </span>
                }
                hint="Vale antigo que o cliente ainda pode gastar."
              />
              <StatTile
                label="Vale antigo emitido"
                value={
                  <span className="font-(--font-mono) tabular-nums">
                    {money(data.issued)}
                  </span>
                }
              />
            </div>

            {customers.length === 0 ? (
              <p className="text-(length:--text-sm) text-(--text-muted)">
                Nenhum vale da gestão antiga até agora.
              </p>
            ) : (
              <TableContainer>
                <Table aria-label="Gestão antiga por cliente">
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Cliente</TableHeaderCell>
                      <TableHeaderCell className="text-right">
                        Trocas
                      </TableHeaderCell>
                      <TableHeaderCell className="text-right">
                        Emitido
                      </TableHeaderCell>
                      <TableHeaderCell className="text-right">
                        Já usado
                      </TableHeaderCell>
                      <TableHeaderCell className="text-right">
                        Em aberto
                      </TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customers.map((row) => (
                      <TableRow key={row.partyId}>
                        <TableCell>{row.name}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {String(row.exchanges)}
                        </TableCell>
                        <TableCell className="text-right font-(--font-mono) tabular-nums">
                          {money(row.issued)}
                        </TableCell>
                        <TableCell className="text-right font-(--font-mono) tabular-nums">
                          {money(row.used)}
                        </TableCell>
                        <TableCell className="text-right font-(--font-mono) tabular-nums">
                          {money(row.open)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </div>
        )}
      </QueryState>
    </Section>
  )
}
